import { useState, useEffect } from "react";
import { useUser } from "../../user_context";
import { API_ENDPOINTS } from "../../api/config";
import JSLayout from "../jobseeker/js_layout";
import EmpLayout from "../employer/emp_layout";

const css = `
  .adm-fade > * { opacity: 0; animation: admFadeUp 0.4s ease forwards; }
  .adm-fade > *:nth-child(1) { animation-delay: 0.02s; }
  .adm-fade > *:nth-child(2) { animation-delay: 0.06s; }
  .adm-fade > *:nth-child(3) { animation-delay: 0.1s; }
  @keyframes admFadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

  .adm-stats-row {
    display: grid; grid-template-columns: repeat(4, 1fr); gap: 0.85rem;
    margin-bottom: 1.75rem;
  }
  .adm-stat-card {
    background: #fff; border: 1px solid rgba(var(--color-text-rgb),0.07); border-radius: 4px;
    padding: 1.15rem 1.35rem;
  }
  .adm-stat-value {
    font-family: 'DM Sans', sans-serif; font-size: 2rem; font-weight: 600;
    color: var(--color-text); line-height: 1; margin-bottom: 4px;
  }
  .adm-stat-label {
    font-size: 0.62rem; font-weight: 500; letter-spacing: 0.08em;
    text-transform: uppercase; color: #aaa;
  }
  .adm-stat-sub {
    font-size: 0.72rem; color: #888; margin-top: 6px; font-weight: 300;
  }

  .adm-section-title {
    font-family: 'DM Sans', sans-serif; font-size: 1.2rem; font-weight: 600;
    color: var(--color-text); margin: 0 0 1rem;
  }
  .adm-section-title em { font-style: normal; color: var(--color-primary); }

  .adm-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem; margin-bottom: 1.75rem; }
  .adm-card {
    background: #fff; border: 1px solid rgba(var(--color-text-rgb),0.07); border-radius: 4px;
    padding: 1.5rem;
  }

  .adm-breakdown-row {
    display: flex; justify-content: space-between; align-items: center;
    padding: 0.6rem 0; border-bottom: 1px solid rgba(var(--color-text-rgb),0.05);
    font-size: 0.82rem;
  }
  .adm-breakdown-row:last-child { border-bottom: none; }
  .adm-breakdown-label { color: #555; font-weight: 400; }
  .adm-breakdown-val { color: var(--color-text); font-weight: 500; font-family: 'DM Sans', sans-serif; font-size: 1.05rem; }

  .adm-chart-bars {
    display: flex; gap: 3px; align-items: flex-end; height: 140px; padding: 0 0.1rem;
  }
  .adm-chart-bar-col { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: flex-end; height: 100%; }
  .adm-chart-bar {
    width: 100%; background: var(--color-primary); border-radius: 2px 2px 0 0; min-height: 2px; opacity: 0.85;
    transition: opacity 0.15s;
  }
  .adm-chart-bar:hover { opacity: 1; }
  .adm-chart-axis {
    display: flex; justify-content: space-between; font-size: 0.62rem; color: #bbb; margin-top: 6px;
  }

  .adm-loading, .adm-error {
    text-align: center; padding: 4rem 1rem; color: #888; font-size: 0.9rem;
  }

  @media (max-width: 1100px) { .adm-stats-row { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 900px) { .adm-grid2 { grid-template-columns: 1fr; } }
  @media (max-width: 500px) { .adm-stats-row { grid-template-columns: 1fr 1fr; } }
`;

function TrendChart({ title, data }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  const step = Math.ceil(data.length / 10);
  return (
    <div className="adm-card">
      <div className="adm-section-title">{title}</div>
      <div className="adm-chart-bars">
        {data.map((d) => (
          <div className="adm-chart-bar-col" key={d.date} title={`${d.date}: ${d.count}`}>
            <div
              className="adm-chart-bar"
              style={{ height: `${(d.count / max) * 100}%` }}
            />
          </div>
        ))}
      </div>
      <div className="adm-chart-axis">
        {data.map((d, i) =>
          i % step === 0 ? <span key={d.date}>{d.date.slice(5)}</span> : null
        )}
      </div>
    </div>
  );
}

export default function AdminMetrics() {
  const { userData } = useUser();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  if (typeof document !== "undefined" && !document.getElementById("bl-adm-css")) {
    const s = document.createElement("style");
    s.id = "bl-adm-css";
    s.textContent = css;
    document.head.appendChild(s);
  }

  useEffect(() => {
    const load = async () => {
      const token = localStorage.getItem("token");
      try {
        const res = await fetch(API_ENDPOINTS.ADMIN_METRICS, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Failed to load platform metrics.");
        const data = await res.json();
        setMetrics(data);
      } catch (err) {
        setError(err.message || "Something went wrong.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const Layout = userData?.role === "employer" ? EmpLayout : JSLayout;

  return (
    <Layout title="Administration" titleEm="Metrics">
      {loading && <div className="adm-loading">Loading platform metrics…</div>}
      {!loading && error && <div className="adm-error">{error}</div>}

      {!loading && !error && metrics && (
        <div className="adm-fade">
          <div className="adm-stats-row">
            <div className="adm-stat-card">
              <div className="adm-stat-value">{metrics.totals.totalUsers}</div>
              <div className="adm-stat-label">Total users</div>
              <div className="adm-stat-sub">{metrics.totals.verifiedUsers} verified</div>
            </div>
            <div className="adm-stat-card">
              <div className="adm-stat-value">{metrics.totals.totalJobSeekers}</div>
              <div className="adm-stat-label">Job seekers</div>
            </div>
            <div className="adm-stat-card">
              <div className="adm-stat-value">{metrics.totals.totalEmployers}</div>
              <div className="adm-stat-label">Employers</div>
            </div>
            <div className="adm-stat-card">
              <div className="adm-stat-value">{metrics.totals.totalListings}</div>
              <div className="adm-stat-label">Job listings</div>
              <div className="adm-stat-sub">{metrics.totals.activeListings} active</div>
            </div>
          </div>

          <div className="adm-grid2">
            <div className="adm-card">
              <div className="adm-section-title">Listings by <em>status</em></div>
              <div className="adm-breakdown-row">
                <span className="adm-breakdown-label">Active</span>
                <span className="adm-breakdown-val">{metrics.totals.activeListings}</span>
              </div>
              <div className="adm-breakdown-row">
                <span className="adm-breakdown-label">Paused</span>
                <span className="adm-breakdown-val">{metrics.totals.pausedListings}</span>
              </div>
              <div className="adm-breakdown-row">
                <span className="adm-breakdown-label">Closed</span>
                <span className="adm-breakdown-val">{metrics.totals.closedListings}</span>
              </div>
              <div className="adm-breakdown-row">
                <span className="adm-breakdown-label">Draft</span>
                <span className="adm-breakdown-val">{metrics.totals.draftListings}</span>
              </div>
            </div>

            <div className="adm-card">
              <div className="adm-section-title">Applications <em>({metrics.totals.totalApplications})</em></div>
              {metrics.totals.applicationsByStatus.length === 0 && (
                <div className="adm-breakdown-row">
                  <span className="adm-breakdown-label">No applications yet</span>
                </div>
              )}
              {metrics.totals.applicationsByStatus.map((s) => (
                <div className="adm-breakdown-row" key={s.status}>
                  <span className="adm-breakdown-label">{s.status}</span>
                  <span className="adm-breakdown-val">{s.count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="adm-stats-row" style={{ gridTemplateColumns: "repeat(2, 1fr)" }}>
            <div className="adm-stat-card">
              <div className="adm-stat-value">{metrics.totals.totalMarketplaceListings}</div>
              <div className="adm-stat-label">Marketplace listings</div>
            </div>
            <div className="adm-stat-card">
              <div className="adm-stat-value">{metrics.totals.totalSellers}</div>
              <div className="adm-stat-label">Marketplace sellers</div>
            </div>
          </div>

          <div className="adm-grid2">
            <TrendChart title={<>Signups — <em>last 30 days</em></>} data={metrics.trends.signups} />
            <TrendChart title={<>Applications — <em>last 30 days</em></>} data={metrics.trends.applications} />
          </div>
        </div>
      )}
    </Layout>
  );
}
